import React, { memo, ReactNode, useCallback, useMemo, useRef } from 'react';
import { noop, useHasScrollbar, useIsScrolling } from '@direct-frontend/stdlib';
import { DOMProps } from '@react-types/shared';
import { getPlatformStyles, isDesktop, isMobile } from '@direct-frontend/platform-code';

import { TestProps } from '../../internal/types/testTypes';
import { cn, getClassNames } from '../../lib/css/className/className';
import { VStack } from '../Stack';
import { Drawer, DrawerHeader } from '../Drawer';
import { List } from '../List';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

import { SidebarExpandButton } from './SidebarExpandButton';
import { SidebarResizer } from './SidebarResizer';
import { SidebarPopupStateProvider } from './SidebarPopupStateProvider';
import { SidebarCollapsedStateProvider } from './SidebarCollapsedStateProvider';
import { SidebarIsNewMobileViewStateProvider } from './SidebarIsNewMobileViewStateProvider';
import { i18n } from './Sidebar.i18n';

getPlatformStyles({
    common: import('./Sidebar.css'),
    mobile: import('./Sidebar@mobile.css'),
    desktop: import('./Sidebar@desktop.css'),
});

const cls = cn('Sidebar');

type SidebarCollapsedProps =
    | {
          /**
           * Логотип сервиса
           */
          logo: {
              /**
               * Полная версия логотипа
               */
              default: ReactNode;
              collapsed?: never;
          };
          onToggleIsCollapsed?: never;
          isCollapsed?: never;
          /**
           * Запрещает сжимать сайдбар
           */
          withoutCollapsed: true;
      }
    | {
          /**
           * Логотип сервиса
           */
          logo: {
              /**
               * Полная версия логотипа
               */
              default: ReactNode;
              /**
               * Версия логотипа для свёрнутого меню
               */
              collapsed: ReactNode;
          };
          /**
           * Коллбек на изменение свёрнутости сайдбара
           */
          onToggleIsCollapsed: () => void;
          /**
           * Состояние свёрнутости сайдбара
           */
          isCollapsed: boolean;
          /**
           * Запрещает сжимать сайдбар
           */
          withoutCollapsed?: false;
      };

export type SidebarProps = {
    /**
     * Дочерние элементы
     * @expectedNode `SidebarSection`
     */
    children?: ReactNode;
    /**
     * Дополнительный класс для сайдбара
     */
    className?: string;
    /**
     * Состояние открытости мобильного сайдбара
     */
    isOpen?: boolean;
    /**
     * Коллбек на изменение открытости мобильного сайдбара
     */
    onToggleIsOpen?: (value: boolean) => void;
    /**
     * Класс для указания ширины сайдбара
     */
    widthClassName: string;
    /**
     * Доступен ли новый вид мобильного сайдбара
     */
    isNewMobileViewAvailable?: boolean;
} & TestProps &
    DOMProps &
    SidebarCollapsedProps;

/**
 * Компонент для отображения сайдбара с функцией меню для сервисов РТ
 */
export const Sidebar = memo<SidebarProps>(function Sidebar({
    children,
    className,
    'data-testid': dataTestId = 'Sidebar',
    logo,
    isCollapsed: initialIsCollapsed = false,
    isOpen,
    onToggleIsCollapsed,
    onToggleIsOpen,
    widthClassName,
    withoutCollapsed = false,
    isNewMobileViewAvailable = false,
}) {
    const isCollapsed = isMobile() || withoutCollapsed ? false : initialIsCollapsed;
    const { default: defaultLogo, collapsed: collapsedLogo } = logo;
    const sidebarRef = useRef<HTMLDivElement>(null);
    const layoutRef = useRef<HTMLDivElement>(null);

    const { isScrolling, onScroll } = useIsScrolling();
    const { hasScrollbar, setRef: scrollableAreaRef } = useHasScrollbar('y');
    const colorSkinClass = getColorSkin('gray-transparent-contour', {});

    const sidebarContent = useMemo(
        () => (
            <SidebarIsNewMobileViewStateProvider value={isNewMobileViewAvailable}>
                <SidebarCollapsedStateProvider
                    isCollapsed={isCollapsed}
                    onToggleIsCollapsed={onToggleIsCollapsed || noop}
                >
                    <SidebarPopupStateProvider layoutRef={layoutRef} initId="">
                        <VStack
                            className={cls(
                                {
                                    collapsed: isCollapsed,
                                    scrolling: isScrolling,
                                    hasScrollbar: hasScrollbar,
                                },
                                [className, widthClassName, colorSkinClass],
                            )}
                            data-testid={isCollapsed ? `${dataTestId}.Collapsed` : dataTestId}
                            ref={sidebarRef}
                        >
                            {isDesktop() && (
                                <VStack data-testid={`${dataTestId}.Logo`} alignItems="center">
                                    {isCollapsed ? collapsedLogo : defaultLogo}
                                </VStack>
                            )}
                            <VStack
                                scrollable
                                className={cls('scrollableArea')}
                                data-testid={`${dataTestId}.ScrollableArea`}
                                ref={scrollableAreaRef}
                                onScroll={onScroll}
                            >
                                <VStack
                                    ref={layoutRef}
                                    className={getClassNames([
                                        cls('layout', { newMobileView: isNewMobileViewAvailable }),
                                        cls('width', [widthClassName]),
                                    ])}
                                >
                                    {children}
                                </VStack>
                            </VStack>
                            {isDesktop() && !withoutCollapsed && (
                                <>
                                    <SidebarExpandButton className={cls('layout')} />
                                    <SidebarResizer />
                                </>
                            )}
                        </VStack>
                    </SidebarPopupStateProvider>
                </SidebarCollapsedStateProvider>
            </SidebarIsNewMobileViewStateProvider>
        ),
        [
            isNewMobileViewAvailable,
            isCollapsed,
            onToggleIsCollapsed,
            isScrolling,
            hasScrollbar,
            className,
            widthClassName,
            dataTestId,
            collapsedLogo,
            defaultLogo,
            scrollableAreaRef,
            onScroll,
            children,
            withoutCollapsed,
            colorSkinClass,
        ],
    );

    const onMobileClose = useCallback(() => onToggleIsOpen && onToggleIsOpen(false), [onToggleIsOpen]);

    if (isMobile()) {
        if (isNewMobileViewAvailable) {
            return (
                <Drawer
                    className={cls('drawer', { newMobileView: isNewMobileViewAvailable })}
                    data-testid={dataTestId}
                    direction="bottom"
                    isOpen={isOpen}
                    onClose={onMobileClose}
                    header={<DrawerHeader onCancelClick={onMobileClose} title={i18n('Меню')} />}
                >
                    <List width="max" size="m">
                        {sidebarContent}
                    </List>
                </Drawer>
            );
        }
        return (
            <Drawer
                className={cls('drawer')}
                data-testid={isOpen ? `${dataTestId}.Open` : dataTestId}
                direction="left"
                isOpen={isOpen}
                onClose={onMobileClose}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    return <>{sidebarContent}</>;
});
